/**
 * Typed client for the /api/auth/* admin endpoints (backend/auth/routes.py).
 *
 * Kept out of lib/api.ts deliberately — that file is append-heavy across
 * parallel branches; auth admin is a self-contained surface. Same-origin
 * fetch, JSON in/out, FastAPI error envelope ({"detail": ...}) surfaced as
 * Error.message, and 401s emit the shared session-expired event so a stale
 * admin session routes back to the Login page like every other API call.
 */
import { emitSessionExpired } from './authEvents'

export type CpUser = {
  email: string
  display_name: string | null
  is_admin: boolean
  can_write_ops: boolean
  can_write_ads: boolean
  created_at: string | null
  last_login_at: string | null
  disabled: boolean
  /** Server-side classification (backend/auth/account_kinds.py, PR 7 / D4):
   *  the Test Account and a disabled duplicate login are marked, never
   *  removed. Optional so an older backend still type-checks. */
  account_kind?: 'person' | 'test' | 'duplicate'
  /** Why it was marked, in plain words; null for a person. */
  account_note?: string | null
}

/** A row of GET /api/auth/service-tokens (admin only; never the token or
 *  its hash). kind 'automation' = admin-managed; 'mcp' = a personal AI-tool
 *  token minted by owner_email (migration 079). */
export type CpServiceToken = {
  name: string
  can_write_ops: boolean
  can_write_ads: boolean
  created_at: string | null
  last_used_at: string | null
  disabled: boolean
  owner_email: string | null
  kind: string | null
}

export type CpUserCreateIn = {
  email: string
  display_name?: string | null
  is_admin?: boolean
  can_write_ops?: boolean
  can_write_ads?: boolean
}

export type CpUserPatchIn = {
  display_name?: string | null
  is_admin?: boolean
  can_write_ops?: boolean
  can_write_ads?: boolean
  disabled?: boolean
  reset_password?: boolean
}

async function authReq<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (!res.ok) {
    if (res.status === 401) emitSessionExpired()
    let detail = `${res.status} ${res.statusText}`
    try {
      const body = await res.json()
      if (typeof body?.detail === 'string') detail = body.detail
    } catch { /* keep the status-line message */ }
    throw new Error(detail)
  }
  return res.json()
}

export const authAdminApi = {
  listUsers: () => authReq<{ users: CpUser[] }>('/api/auth/users'),

  /** GET /api/auth/service-tokens — admin only (403 for anyone else with
   *  auth on). Read-only here: the Users tab lists them, nothing more. */
  listServiceTokens: () => authReq<{ service_tokens: CpServiceToken[] }>('/api/auth/service-tokens'),

  /** POST /api/auth/users — backend GENERATES the temp password and returns
   *  it exactly once in this response. */
  createUser: (data: CpUserCreateIn) =>
    authReq<{ email: string; temp_password: string }>('/api/auth/users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /** PATCH /api/auth/users/{email}. With reset_password:true the response
   *  carries a fresh temp_password (shown once; old sessions revoked). */
  patchUser: (email: string, patch: CpUserPatchIn) =>
    authReq<{ email: string; updated: string[]; temp_password?: string }>(
      `/api/auth/users/${encodeURIComponent(email)}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    ),

  /** DELETE /api/auth/users/{email} — HARD-delete an account (admin only).
   *  Destructive, no undo: removes the user + all their sessions, revokes
   *  every service token they own, and KEEPS their feedback but NULLs the
   *  attribution. Refused (409) for the last admin or self-delete; 404 if
   *  the user is gone. Returns the counts removed/de-attributed. */
  deleteUser: (email: string) =>
    authReq<{
      email: string
      deleted: boolean
      sessions_deleted: number
      tokens_deleted: number
      feedback_deattributed: number
    }>(`/api/auth/users/${encodeURIComponent(email)}`, { method: 'DELETE' }),

  /** POST /api/auth/change-password — self-service for the LOGGED-IN user
   *  (admin or not). Re-auths with current_password server-side; the
   *  current session is intentionally NOT revoked. 400 ("current password
   *  is incorrect") surfaces as Error.message via the shared envelope. */
  changePassword: (current_password: string, new_password: string) =>
    authReq<{ ok: boolean }>('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password, new_password }),
    }),
}
